<template>
  <div
    v-if="
      quickAccessModule &&
      quickAccessModule.article_info &&
      quickAccessModule.article_info.length > 0
    "
    class="quick-access-section"
  >
    <swiper
      :slides-per-view="'auto'"
      :space-between="5"
      :free-mode="true"
      class="quick-access-swiper"
    >
      <swiper-slide
        v-for="(article, index) in quickAccessModule.article_info"
        :key="index"
        class="quick-access-item"
        @click="handleArticleClick(article)"
      >
        <!-- 图片 -->
        <div class="article-image">
          <img
            :src="article.img_url"
            :alt="article.article_title"
            @error="handleImageError"
          />
        </div>
        <!-- 标题 -->
        <div class="article-title" v-if="article.article_title">
          {{ article.article_title }}
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/free-mode";
import sdkBridge from "@/utils/sdkBridge";
import { getGameConfig } from "@/config/gameConfig";
import { useForumStore, useLanguageStore } from "@/stores";

interface ArticleInfo {
  img_url: string;
  article_title: string;
  article_url: string;
  sub_title?: string;
  jump_type?: number;
  page_id?: string;
}

enum ModuleType {
  SHORT_IMAGE_SINGLE_TEXT = "1", // 短图单文案
  LONG_IMAGE_SINGLE_TEXT = "2", // 长图单文案
  QUICK_ACCESS = "3", // 金刚区
  LEFT_IMAGE_RIGHT_DOUBLE_TEXT = "4", // 左图右双文案
}

const forumStore = useForumStore();
const languageStore = useLanguageStore();
const router = useRouter();

// 过滤出 module_type 为 3 的模块
const quickAccessModule = computed(() => {
  if (!forumStore.customModules) return null;
  return (
    forumStore.customModules.find(
      (module) => module.module_type === ModuleType.QUICK_ACCESS
    ) || null
  );
});

// 处理文章点击
const handleArticleClick = (article: ArticleInfo) => {
  // 如果 jumpType 为 2，跳转到 page 路由
  // 验证 page_id 存在且非空
  if (article.jump_type === 2 && article.page_id) {
    router.push({
      path: "/page",
      query: {
        pageId: article.page_id,
      },
    });
    return;
  }

  // 其他情况保持原有逻辑
  if (article.article_url) {
    sdkBridge.open(article.article_url);
  }
};

// 处理图片加载错误
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.src =
    languageStore.getImage("iconEmpty") || getGameConfig().iconEmpty;
};
</script>

<style lang="scss" scoped>
.quick-access-section {
  padding: 10px 12px;
  margin: 0 auto;
  max-width: 800px;

  .quick-access-swiper {
    width: 100%;
    margin: 0;
    position: relative;
    overflow: hidden;

    :deep(.swiper-wrapper) {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
    }
  }

  .quick-access-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    width: auto !important;
    min-width: 80px;
    transition: transform 0.2s ease;
    .article-image {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      overflow: hidden;
      margin-bottom: 8px;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .article-title {
      font-size: 11px;
      color: #333;
      line-height: 1.4;
      text-align: center;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

@media (min-width: 768px) {
  .quick-access-section {
    .quick-access-swiper {
      :deep(.swiper-wrapper) {
        justify-content: space-around;
      }
    }

    .quick-access-item {
      .article-title {
        font-size: 14px;
      }
    }
  }
}
</style>
